// sessionExpiredUI.ts
// Called by IndexPage.tsx and IndexPageGuest.tsx when the session expires

// session timeout 
import './sessionExpiredUI.css';

const SESSION_EXPIRED_LOCK_KEY = 'relay_session_expired_lock';

export function setSessionExpiredLock(): void {
  try {
    sessionStorage.setItem(SESSION_EXPIRED_LOCK_KEY, '1');
  } catch {
    // ignore storage failures
  }
}

export function clearSessionExpiredLock(): void {
  try {
    sessionStorage.removeItem(SESSION_EXPIRED_LOCK_KEY);
  } catch {
    // ignore storage failures
  }
}

export function isSessionExpiredLocked(): boolean {
  try {
    return sessionStorage.getItem(SESSION_EXPIRED_LOCK_KEY) === '1';
  } catch {
    return false;
  }
}

export function showSessionExpiredUI(): void {
  setSessionExpiredLock();
  const root = document.getElementById('root');
  if (!root) return;

  // Pull selected bot info from storage to theme the UI
  const storedName = localStorage.getItem('selectedBotName') || undefined;
  const storedAvatar = localStorage.getItem('selectedBotAvatar') || undefined;
  const storedThemeBg = localStorage.getItem('selectedBotThemeBg') || undefined;
  const storedThemeFg = localStorage.getItem('selectedBotThemeFg') || undefined;
  const storedFooterBg = localStorage.getItem('selectedBotFooterBg') || undefined;
  const storedBotRaw = localStorage.getItem('SelectedBot');
  const storedBotParsed = (() => {
    try {
      return storedBotRaw ? JSON.parse(storedBotRaw) : null;
    } catch {
      return null;
    }
  })();
  const storedBotSettingsLogo = (() => {
    try {
      const botSettings = localStorage.getItem('botSettings');
      if (!botSettings) return undefined;
      const parsed = JSON.parse(botSettings);
      return parsed?.logo;
    } catch {
      return undefined;
    }
  })();

  const activeBot = (window as any)?.selectedBot || storedBotParsed;
  const storedBotId =
    storedBotParsed?.botid ||
    storedBotParsed?.id ||
    activeBot?.botid ||
    activeBot?.id ||
    localStorage.getItem('selectedBot') ||
    undefined;
  const avatarMatchesBot = (url?: string, botId?: string | null) =>
    Boolean(url && botId && url.includes(String(botId)));
  const logoMatchesBot = (logo?: string, botId?: string | null, name?: string | null) =>
    Boolean(
      logo &&
        ((botId && logo.includes(String(botId))) ||
          (name && logo.toLowerCase().includes(String(name).toLowerCase())))
    );

  let botThemeBg =
    storedFooterBg ||
    storedThemeBg ||
    activeBot?.footer?.backcolor ||
    activeBot?.theme?.backgroundColor ||
    '#002A48';
  let botThemeFg = storedThemeFg || activeBot?.theme?.foregroundColor || '#FFFFFF';
  let botName =
    storedName ||
    activeBot?.name ||
    activeBot?.title ||
    'AI Assistant';
  const candidateStoredAvatar =
    storedBotParsed?.avatar && avatarMatchesBot(storedBotParsed?.avatar, storedBotId)
      ? storedBotParsed?.avatar
      : '';
  const candidateActiveAvatar =
    activeBot?.avatar && avatarMatchesBot(activeBot?.avatar, storedBotId)
      ? activeBot?.avatar
      : '';
  let botAvatar = storedAvatar || candidateStoredAvatar || candidateActiveAvatar || '';
  // If still not matched, try botSettings logo; otherwise clear to avoid stale assets
  if (!avatarMatchesBot(botAvatar, storedBotId)) {
    botAvatar = logoMatchesBot(storedBotSettingsLogo, storedBotId, botName)
      ? storedBotSettingsLogo
      : '';
  } else if (!botAvatar && logoMatchesBot(storedBotSettingsLogo, storedBotId, botName)) {
    botAvatar = storedBotSettingsLogo;
  }
  let botFooterBg = storedFooterBg || activeBot?.footer?.backcolor || '';
  if (storedBotParsed) {
    botThemeBg = storedBotParsed?.footer?.backcolor || storedBotParsed?.theme?.backgroundColor || botThemeBg;
    botThemeFg = storedBotParsed?.theme?.foregroundColor || botThemeFg;
    botName = storedBotParsed?.name || botName || storedBotParsed?.title || 'AI Assistant';
    botFooterBg = storedBotParsed?.footer?.backcolor || botFooterBg;
  }

  // Try org settings for colors
  try {
    const orgRaw = localStorage.getItem('org');
    if (orgRaw) {
    const org = JSON.parse(orgRaw);
    const orgBg =
      org?.headerBackColor ||
      org?.containerStyle?.backgroundColor ||
      org?.footer?.backcolor;
    const orgFg = org?.bodyStyle?.color;

    // Only override if org supplies a value; keep contrast if they match.
    botThemeBg = orgBg || botThemeBg;
    botThemeFg = orgFg || botThemeFg;
    botFooterBg = org?.footer?.backcolor || botFooterBg;
  }
  } catch {
    // ignore parse errors
  }

  /*console.log('[session-expired][debug]', {
    storedName,
    storedAvatar,
    storedThemeBg,
    storedThemeFg,
    storedFooterBg,
    storedBotSettingsLogo,
    activeBot,
    storedBotId,
    botThemeBg,
    botThemeFg,
    botName,
    botAvatar,
    botFooterBg,
    rootPresent: !!root,
  });
*/
  // Apply theme to document-level CSS variables so CSS picks it up.
  // Prefer footer backcolor for all surfaces to keep it consistent.
  const rootEl = document.documentElement;
  const bodyBg = botFooterBg || botThemeBg;
  const bodyFg = botThemeFg === bodyBg ? '#FFFFFF' : botThemeFg;
  rootEl.style.setProperty('--exp-bg', bodyBg);
  rootEl.style.setProperty('--exp-fg', bodyFg);
  document.body.style.backgroundColor = bodyBg;
  document.body.style.color = bodyFg;
  if (root) {
    root.style.backgroundColor = bodyBg;
    root.style.color = bodyFg;
    root.style.minHeight = '100vh';
  }


  // prevent multiple renders
  if (document.getElementById('session-expired')) return;
  root.innerHTML = `
  <header id="exp-header" class="exp-header" style="background:${bodyBg};color:${bodyFg};">
   <h2>
        <img id="expired-logo" src="${storedBotSettingsLogo || '/favicon.png'}"
             alt="Centerbeam AI"
             class="disconnectImage">&nbsp;${botName}
    </h2>
    </header>

    <br>
 <div id="session-expired" style="background:${bodyBg};color:${bodyFg};">   
      ${botAvatar ? `<div style="display:flex;flex-direction:column;align-items:center;gap:0.5rem;margin-bottom:1rem;">
        <img src="${botAvatar}" alt="${botName}" style="width:96px;height:96px;border-radius:50%;object-fit:cover;border:2px solid ${bodyFg};background:${bodyBg};" />
        <div style="font-size:1rem;font-weight:600;color:${bodyFg};">${botName}</div>` : ''}
      <h2>Your session has timed out.</h2>
      <h3>Want to start another session?</h3>

     <div class="button-row">
        <button id="reconnectButton" title="Yes">Yes</button>
        <!--<button id="portalButton" title="No">No</button>-->
        </div>
     </div>
    </div>
  `;


  // attach events after the HTML is injected
  const reconnectBtn = document.getElementById('reconnectButton');
  const portalBtn = document.getElementById('portalButton');

  reconnectBtn?.addEventListener('click', () => {
    clearSessionExpiredLock();
    if ((window as any).__ping) {
      (window as any).__ping.connectionAttempts = 1;
      (window as any).__ping.pingTimestamp = Date.now();
    }
    try {
      localStorage.setItem('pingTimestamp', String((window as any).__ping?.pingTimestamp || Date.now()));
    } catch {
      // ignore storage failures
    }
    //window.location.reload();
    // Get current origin (protocol + host + port)
    const origin = window.location.origin;

    // Get full querystring (e.g. ?id=123&ref=home)
    const params = new URLSearchParams(window.location.search);
    // Ensure current session id (if available) is preserved on reconnect
    const storedSes =
      sessionStorage.getItem('webSessionId') || localStorage.getItem('webSessionId');
    if (storedSes && !params.has('ses')) {
      params.append('ses', storedSes);
    }
    const query = params.toString();

    // Redirect to /signin on the same server/port, preserving querystring
    window.location.href = `${origin}/signin${query ? `?${query}` : ''}`;

  });

  portalBtn?.addEventListener('click', () => {
    window.location.href = 'https://video.ribuspark.com';
  });
}
